import { FormSchema } from '../../../types/formSchema';
import { createParentSection, createLeafSection, createParentSectionWithFilters } from '../../../utils/documentSectionHelpers';
import { ACTUALS_LABEL, ACTUALS_VALUE, USD_CONSTANT_LABEL, USD_CONSTANT_VALUE } from '../../../constants/mfr-constants';

export const mfrFormSchema: FormSchema = {
  id: 'mfr-create',
  title: 'Create MFR Report',
  description: 'Configure your MFR report with the required filters and document sections.',
  sections: [
    {
      title: 'Basic Information',
      description: 'Configure the basic information to start your report generation',
      layout: 'single-column',
      fields: [
        {
          type: 'text',
          name: 'name',
          label: 'Name',
          placeholder: 'Enter name...',
          validation: [
            { required: true, message: 'Name is required' }
          ],
          componentProps: {
            allowClear: true,
            showCount: true,
            maxLength: 100
          }
        },
        {
          type: 'textarea',
          name: 'description',
          label: 'Description',
          placeholder: 'Enter description...',
          componentProps: {
            allowClear: true,
            showCount: true,
            maxLength: 500,
            autoSize: { minRows: 3, maxRows: 6 }
          }
        }
      ]
    },
    {
      title: 'Filters',
      layout: 'two-column',
      fields: [
        {
          type: 'treeSelect',
          name: 'marketplace',
          label: 'Marketplace',
          placeholder: 'Select marketplace...',
          multiple: true,
          maxTagCount: 2,
          treeDefaultExpandAll: false,
          treeData: [
            {
              title: 'EU5',
              value: 'eu5',
              key: 'eu5',
              children: [
                {
                  title: 'DE - Germany',
                  value: 'de',
                  key: 'de'
                },
                {
                  title: 'ES - Spain',
                  value: 'es',
                  key: 'es'
                },
                {
                  title: 'FR - France',
                  value: 'fr',
                  key: 'fr'
                },
                {
                  title: 'IT - Italy',
                  value: 'it',
                  key: 'it'
                },
                {
                  title: 'UK - United Kingdom',
                  value: 'uk',
                  key: 'uk'
                }
              ]
            },
            {
              title: 'MENA',
              value: 'mena',
              key: 'mena',
              children: [
                {
                  title: 'EG - Egypt',
                  value: 'eg',
                  key: 'eg'
                },
                {
                  title: 'SA - Saudi Arabia',
                  value: 'sa',
                  key: 'sa'
                },
                {
                  title: 'UAE - United Arab Emirates',
                  value: 'uae',
                  key: 'uae'
                }
              ]
            },
            {
              title: 'US - United States',
              value: 'us',
              key: 'us'
            },
            {
              title: 'CA - Canada',
              value: 'ca',
              key: 'ca'
            },
            {
              title: 'JP - Japan',
              value: 'jp',
              key: 'jp'
            },
            {
              title: 'AU - Australia',
              value: 'au',
              key: 'au'
            },
            {
              title: 'IN - India',
              value: 'in',
              key: 'in'
            },
            {
              title: 'BR - Brazil',
              value: 'br',
              key: 'br'
            },
            {
              title: 'MX - Mexico',
              value: 'mx',
              key: 'mx'
            }
          ],
          validation: [
            { required: true, message: 'Marketplace is required' }
          ]
        },
        {
          type: 'multiselect',
          name: 'channels',
          label: 'Channel',
          placeholder: 'Select channels...',
          options: [
            { value: 'fba', label: 'FBA' },
            { value: 'mfn', label: 'MFN' },
            { value: 'retail', label: 'Retail' }
          ],
          validation: [
            { required: true, message: 'Channel is required' }
          ]
        },
        {
          type: 'treeSelect',
          name: 'product_lines',
          label: 'Product Line',
          placeholder: 'Select product line...',
          treeDefaultExpandAll: false,
          showSearch: true,
          allowClear: true,
          loadData: true,
          filterType: 'productid',
          treeData: [
            { 
              value: "A1PTOT", 
              title: "A1PTOT - Total Products Alternate",
              key: "A1PTOT",
              hierarchyType: "COGNOS_PRODUCT_ALTERNATE_HIERARCHY",
              isRootNode: true,
              isLeaf: false
            },
            { 
              value: "PTOT", 
              title: "PTOT - Total Products",
              key: "PTOT", 
              hierarchyType: "COGNOS_PRODUCT_HIERARCHY",
              isRootNode: true,
              isLeaf: false
            },
            { 
              value: "altProduct", 
              title: "altProduct",
              key: "altProduct",
              hierarchyType: "COGNOS_PRODUCT_ALTERNATE_ALT_HIERARCHY",
              isRootNode: true,
              isLeaf: false
            }
          ],
          validation: [
            { required: true, message: 'Product Line is required' }
          ]
        },
        {
          type: 'treeSelect',
          name: 'periods',
          label: 'Period',
          placeholder: 'Select period...',
          treeDefaultExpandAll: false,
          treeData: [
            {
              title: '2025',
              value: '2025',
              key: '2025',
              children: [
                {
                  title: 'Q3 2025',
                  value: 'q3-2025',
                  key: 'q3-2025',
                  children: [
                    {
                      title: 'Aug 2025',
                      value: 'aug-2025',
                      key: 'aug-2025'
                    },
                    {
                      title: 'Jul 2025',
                      value: 'jul-2025',
                      key: 'jul-2025'
                    }
                  ]
                },
                {
                  title: 'Q2 2025',
                  value: 'q2-2025',
                  key: 'q2-2025',
                  children: [
                    {
                      title: 'Jun 2025',
                      value: 'jun-2025',
                      key: 'jun-2025'
                    },
                    {
                      title: 'May 2025',
                      value: 'may-2025',
                      key: 'may-2025'
                    },
                    {
                      title: 'Apr 2025',
                      value: 'apr-2025',
                      key: 'apr-2025'
                    }
                  ]
                },
                {
                  title: 'Q1 2025',
                  value: 'q1-2025',
                  key: 'q1-2025',
                  children: [
                    {
                      title: 'Mar 2025',
                      value: 'mar-2025',
                      key: 'mar-2025'
                    },
                    {
                      title: 'Feb 2025',
                      value: 'feb-2025',
                      key: 'feb-2025'
                    },
                    {
                      title: 'Jan 2025',
                      value: 'jan-2025',
                      key: 'jan-2025'
                    }
                  ]
                }
              ]
            },
            {
              title: '2024',
              value: '2024',
              key: '2024',
              children: [
                {
                  title: 'Q4 2024',
                  value: 'q4-2024',
                  key: 'q4-2024',
                  children: [
                    {
                      title: 'Dec 2024',
                      value: 'dec-2024',
                      key: 'dec-2024'
                    },
                    {
                      title: 'Nov 2024',
                      value: 'nov-2024',
                      key: 'nov-2024'
                    },
                    {
                      title: 'Oct 2024',
                      value: 'oct-2024',
                      key: 'oct-2024'
                    }
                  ]
                },
                {
                  title: 'Q3 2024',
                  value: 'q3-2024',
                  key: 'q3-2024',
                  children: [
                    {
                      title: 'Sep 2024',
                      value: 'sep-2024',
                      key: 'sep-2024'
                    },
                    {
                      title: 'Aug 2024',
                      value: 'aug-2024',
                      key: 'aug-2024'
                    },
                    {
                      title: 'Jul 2024',
                      value: 'jul-2024',
                      key: 'jul-2024'
                    }
                  ]
                },
                {
                  title: 'Q2 2024',
                  value: 'q2-2024',
                  key: 'q2-2024',
                  children: [
                    {
                      title: 'Jun 2024',
                      value: 'jun-2024',
                      key: 'jun-2024'
                    },
                    {
                      title: 'May 2024',
                      value: 'may-2024',
                      key: 'may-2024'
                    },
                    {
                      title: 'Apr 2024',
                      value: 'apr-2024',
                      key: 'apr-2024'
                    }
                  ]
                },
                {
                  title: 'Q1 2024',
                  value: 'q1-2024',
                  key: 'q1-2024',
                  children: [
                    {
                      title: 'Mar 2024',
                      value: 'mar-2024',
                      key: 'mar-2024'
                    },
                    {
                      title: 'Feb 2024',
                      value: 'feb-2024',
                      key: 'feb-2024'
                    },
                    {
                      title: 'Jan 2024',
                      value: 'jan-2024',
                      key: 'jan-2024'
                    }
                  ]
                }
              ]
            },
            {
              title: '2023',
              value: '2023',
              key: '2023',
              children: [
                {
                  title: 'Q4 2023',
                  value: 'q4-2023',
                  key: 'q4-2023',
                  children: [
                    {
                      title: 'Dec 2023',
                      value: 'dec-2023',
                      key: 'dec-2023'
                    },
                    {
                      title: 'Nov 2023',
                      value: 'nov-2023',
                      key: 'nov-2023'
                    },
                    {
                      title: 'Oct 2023',
                      value: 'oct-2023',
                      key: 'oct-2023'
                    }
                  ]
                },
                {
                  title: 'Q3 2023',
                  value: 'q3-2023',
                  key: 'q3-2023',
                  children: [
                    {
                      title: 'Sep 2023',
                      value: 'sep-2023',
                      key: 'sep-2023'
                    },
                    {
                      title: 'Aug 2023',
                      value: 'aug-2023',
                      key: 'aug-2023'
                    },
                    {
                      title: 'Jul 2023',
                      value: 'jul-2023',
                      key: 'jul-2023'
                    }
                  ]
                },
                {
                  title: 'Q2 2023',
                  value: 'q2-2023',
                  key: 'q2-2023',
                  children: [
                    {
                      title: 'Jun 2023',
                      value: 'jun-2023',
                      key: 'jun-2023'
                    },
                    {
                      title: 'May 2023',
                      value: 'may-2023',
                      key: 'may-2023'
                    },
                    {
                      title: 'Apr 2023',
                      value: 'apr-2023',
                      key: 'apr-2023'
                    }
                  ]
                },
                {
                  title: 'Q1 2023',
                  value: 'q1-2023',
                  key: 'q1-2023',
                  children: [
                    {
                      title: 'Mar 2023',
                      value: 'mar-2023',
                      key: 'mar-2023'
                    },
                    {
                      title: 'Feb 2023',
                      value: 'feb-2023',
                      key: 'feb-2023'
                    },
                    {
                      title: 'Jan 2023',
                      value: 'jan-2023',
                      key: 'jan-2023'
                    }
                  ]
                }
              ]
            }
          ],
          validation: [
            { required: true, message: 'Period is required' }
          ]
        },
        {
          type: 'dropdown',
          name: 'base_scenario',
          label: 'Base Scenario',
          defaultValue: ACTUALS_VALUE,
          readonly: true,
          options: [
            { value: ACTUALS_VALUE, label: ACTUALS_LABEL }
          ],
          validation: [
            { required: true, message: 'Base Scenario is required' }
          ]
        },
        {
          type: 'multiselect',
          name: 'comparison_scenario',
          label: 'Comparison Scenario',
          placeholder: 'Select comparison scenario...',
          maxTagCount: 2,
          options: [
            { value: 'q3g', label: 'Q3G' },
            { value: 'q2g', label: 'Q2G' },
            { value: 'q1g', label: 'Q1G' },
            { value: 'op2-2025', label: 'OP2 2025' },
            { value: 'op2-2024', label: 'OP2 2024' },
            { value: 'mom', label: 'MoM' },
            { value: 'qoq', label: 'QoQ' },
            { value: 'yoy', label: 'YoY' }
          ],
          validation: [
            { required: true, message: 'Comparison Scenario is required' }
          ]
        },
        {
          type: 'dropdown',
          name: 'currency',
          label: 'Currency',
          defaultValue: USD_CONSTANT_VALUE,
          readonly: true,
          options: [
            { value: USD_CONSTANT_VALUE, label: USD_CONSTANT_LABEL }
          ],
          validation: [
            { required: true, message: 'Currency is required' }
          ]
        }
      ]
    },
    {
      title: 'Document Sections',
      layout: 'single-column',
      fields: [
        {
          type: 'documentSectionsTable',
          name: 'documentSections',
          label: '',
          tableData: [
            // Executive P&L Group
            createParentSection('exec-pl', 'Executive P&L', [
              createLeafSection('executive_pnl_executive_summary', 'Executive Summary'),
              createLeafSection('executive_pnl_pnl_by_channel', 'P&L by Channel'),
              createLeafSection('executive_pnl_pnl_by_gl', 'P&L by GL', {
                hasFilter: true,
                filterType: 'multiselect',
                filterDependsOn: 'productLine',
                filterPlaceholder: 'Select Product Line',
                filterOptions: []
              }),
              createLeafSection('executive_pnl_pnl_by_marketplace', 'P&L by Marketplace')
            ]),
            
            // P&L by Slice Group
            createParentSectionWithFilters('pl-slice', 'P&L by Slice', {
              filterType: 'multiselect',
              filterPlaceholder: 'Add Slice',
              filterOptions: [
                { value: 'pnl_by_slice_asp_band', label: 'ASP Band' },
                { value: 'pnl_by_slice_everyday_essentials', label: 'Everyday Essentials' },
                { value: 'pnl_by_slice_fc_type', label: 'FC Type' },
                { value: 'pnl_by_slice_prime_plan', label: 'Prime Plan' },
                { value: 'pnl_by_slice_private_brands', label: 'Private Brands' }
              ]
            }),
            
            // Business Trends Group
            createParentSection('business-trends', 'Business Trends', [
              createLeafSection('business_trends_top_bottom_brands', 'Top and Bottom 10 Brands'),
              createLeafSection('business_trends_top_bottom_asins', 'Top and Bottom 10 ASINs')
            ]),

            // Cost to Serve (standalone)
            createLeafSection('cost_to_serve', 'Cost to Serve'),

            // Detailed P&L (standalone)
            createLeafSection('detailed_pnl', 'Detailed P&L'),

            // P&L Drill Down Group
            createParentSectionWithFilters('pl-drill-down', 'P&L Drill Down', {
              filterType: 'multiselect',
              filterPlaceholder: 'Add Pnl Line',
              filterOptions: [
                { value: 'pnl_drill_down_net_revenue', label: 'Net Revenue' },
                { value: 'pnl_drill_down_marketplace_revenue', label: 'Marketplace Rev' },
                { value: 'pnl_drill_down_net_fulfillment_revenue', label: 'Net Fulfillment Revenue' },
                { value: 'pnl_drill_down_net_ppm', label: 'Net PPM%' },
                { value: 'pnl_drill_down_vendor_profit', label: 'Vendor Profit' },
                { value: 'pnl_drill_down_gross_profit', label: 'Gross Profit' },
                { value: 'pnl_drill_down_contribution_profit', label: 'Contribution Profit' },
                { value: 'pnl_drill_down_cp_less_ops_field_fixed', label: 'CP Less Ops Field Fixed' },
                { value: 'pnl_drill_down_operating_income', label: 'Operating Income' }
              ]
            })
          ]
        }
      ]
    }
  ]
};
